import { useEffect } from "react";

let openLockCount = 0;
let lockedScrollY = 0;
let releaseScheduled = false;

// Pin the document while an overlay is open. Otherwise, when an input inside a (position: fixed)
// overlay focuses, iOS scrolls the document to chase it into view — the overlay stays put but the
// page slides up behind it. Fixing the body removes the scroll it would chase.
function lockBodyScroll(): void {
  lockedScrollY = window.scrollY;
  const { style } = document.body;
  style.position = "fixed";
  style.top = `-${lockedScrollY}px`;
  style.left = "0";
  style.right = "0";
  style.width = "100%";
}

function unlockBodyScroll(): void {
  const { style } = document.body;
  style.position = "";
  style.top = "";
  style.left = "";
  style.right = "";
  style.width = "";
  window.scrollTo(0, lockedScrollY);
}

// React runs every effect cleanup before any effect setup, so one sheet closing in the same commit
// another opens drops the count to 0 in between. Releasing there replays window.scrollTo against a
// document WebKit has not re-laid-out, which clamps the restore. Defer past the effect flush.
function scheduleRelease(): void {
  if (releaseScheduled) return;

  releaseScheduled = true;
  queueMicrotask(() => {
    releaseScheduled = false;
    if (openLockCount > 0) return;

    delete document.body.dataset["sheetOpen"];
    unlockBodyScroll();
  });
}

/**
 * Locks body scroll while `active`. Reference-counted so stacked overlays (a sheet and the
 * success modal) compose: the body only unlocks once the last consumer closes. A sheet that hands
 * off to another in the same commit never unlocks at all.
 */
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;

    openLockCount += 1;
    document.body.dataset["sheetOpen"] = "true";
    if (openLockCount === 1 && !releaseScheduled) lockBodyScroll();

    return () => {
      openLockCount -= 1;
      if (openLockCount <= 0) {
        openLockCount = 0;
        scheduleRelease();
      }
    };
  }, [active]);
}
